import { useEffect, useId, useRef, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { checkEnv } from '@elevator/ui';
import useStyles from './styles';

// Type helper for GoogleTagSlot (references global type from googletag.d.ts)
type GoogleTagSlot = ReturnType<Window['googletag']['defineSlot']>;

/**
 * Props for the AdSlot component.
 */
export interface AdSlotProps {
  /** The ad unit path (e.g., 'leaderboard', 'sidebar', 'mobile-banner') */
  adUnit: string;
  /** Array of ad sizes in [width, height] format (e.g., [[300, 250], [728, 90]]) */
  sizes: number[][];
  /**
   * Optional responsive size mapping configuration.
   * Maps viewport widths to different ad sizes.
   * @example [{ viewport: [1024, 0], sizes: [[728, 90]] }, { viewport: [0, 0], sizes: [[300, 250]] }]
   */
  sizeMapping?: Array<{
    /** Viewport dimensions [width, height] - height is typically 0 for "and above" */
    viewport: number[];
    /** Ad sizes to use for this viewport range */
    sizes: number[][];
  }>;
  /** Optional custom slot ID. If not provided, a unique ID will be generated */
  slotId?: string;
  /**
   * When true (e.g. when used inside AdSpace), the test-mode placeholder shows the ad unit above the size info.
   * When false or omitted (e.g. in AdSlot stories), only the size name and dimensions are shown.
   */
  showAdUnit?: boolean;
  /** Called when GPT confirms this slot received a creative. */
  onAdFilled?: () => void;
}

/** Returns a human-readable name for common ad dimensions (test mode placeholder only). */
const getSizeName = (width: number, height: number): string => {
  const key = `${width}×${height}`;
  const names: Record<string, string> = {
    '970×250': 'Wide Banner',
    '728×90': 'Leaderboard',
    '336×280': 'Large Rectangle',
    '320×50': 'Mobile Banner',
    '300×600': 'Half Page',
    '300×250': 'Medium Rectangle',
  };
  return names[key] ?? 'Custom';
};

/**
 * AdSlot Component - Direct GPT Integration
 *
 * Drop-in replacement for react-dfp's AdSlot component.
 * Uses Google Publisher Tag (GPT) directly with native lazy loading.
 *
 * Features:
 * - Native GPT lazy loading
 * - Responsive size mapping
 * - Test mode with MUI placeholders for local development
 * - Automatic cleanup on unmount
 */
export const AdSlot = ({
  adUnit,
  sizes,
  sizeMapping,
  slotId,
  showAdUnit = false,
  onAdFilled,
}: AdSlotProps) => {
  const { classes } = useStyles();
  const uniqueId = useId();
  const divId = slotId || `gpt-ad-${adUnit}-${uniqueId.replace(/:/g, '-')}`;
  const slotRef = useRef<GoogleTagSlot | null>(null);

  // Determine if we're in test mode (local development)
  const { isTestMode } = checkEnv();

  // Stay null for the first render so SSR and hydration pick the same placeholder.
  // Measuring `window` here made mobile say "Medium Rectangle" while the server
  // said "Wide Banner".
  const [viewportWidth, setViewportWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!isTestMode || typeof window === 'undefined') return;
    const handleResize = () => setViewportWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isTestMode]);

  useEffect(() => {
    // Skip GPT initialization in test mode
    if (isTestMode) return;

    // Validate GAM_ID in production
    const gamId = process.env.GAM_ID;
    if (!gamId || gamId.length === 0) {
      if (process.env.NODE_ENV === 'production') {
        throw new Error('GAM_ID is not configured in production environment.');
      }
      console.warn('GAM_ID is not configured. Ad slots will not be initialized.');
      return;
    }

    // Ensure googletag is available
    if (typeof window === 'undefined' || !window.googletag) {
      console.warn('googletag is not available. Ad slot will not be initialized.');
      return;
    }

    const handleSlotRenderEnded = (event: { slot: GoogleTagSlot; isEmpty: boolean }) => {
      if (event.slot.getSlotElementId() !== divId || event.isEmpty) return;
      onAdFilled?.();
    };

    try {
      window.googletag.cmd.push(() => {
        try {
          const pubads = window.googletag.pubads();
          pubads.addEventListener('slotRenderEnded', handleSlotRenderEnded);

          // Define the ad slot
          const slot = window.googletag
            .defineSlot(`/${gamId}/${adUnit}`, sizes, divId)
            .addService(pubads);

          // Apply responsive size mapping if provided
          if (sizeMapping && sizeMapping.length > 0) {
            const mapping = window.googletag.sizeMapping();

            sizeMapping.forEach((map) => {
              mapping.addSize(map.viewport, map.sizes);
            });

            slot.defineSizeMapping(mapping.build());
          }

          slotRef.current = slot;

          // Display the ad (lazy loading handled by global config)
          window.googletag.display(divId);
        } catch (error) {
          console.error(`Failed to initialize ad slot ${divId}:`, error);
        }
      });
    } catch (error) {
      console.error(`Failed to push GPT command for slot ${divId}:`, error);
    }

    // Cleanup: Destroy slot when component unmounts
    return () => {
      if (window.googletag) {
        try {
          window.googletag.cmd.push(() => {
            window.googletag
              .pubads()
              .removeEventListener('slotRenderEnded', handleSlotRenderEnded);
          });
        } catch (error) {
          console.error(`Failed to remove slotRenderEnded listener for ${divId}:`, error);
        }
      }
      if (slotRef.current && window.googletag) {
        try {
          window.googletag.cmd.push(() => {
            window.googletag.destroySlots([slotRef.current!]);
          });
        } catch (error) {
          console.error(`Failed to destroy ad slot ${divId}:`, error);
        }
      }
    };
  }, [adUnit, divId, sizes, sizeMapping, isTestMode, onAdFilled]);

  // Test mode: Render MUI placeholder
  if (isTestMode) {
    // Determine size based on viewport and sizeMapping
    let width = 300;
    let height = 250;

    if (sizeMapping && sizeMapping.length > 0 && viewportWidth !== null) {
      // Find the appropriate size mapping for current viewport
      // Size mappings are typically ordered from largest to smallest viewport
      for (const mapping of sizeMapping) {
        if (viewportWidth >= mapping.viewport[0]) {
          const selectedSize = mapping.sizes[0];
          if (selectedSize) {
            [width, height] = selectedSize;
            break;
          }
        }
      }
    } else if (sizes[0]) {
      // Fallback to first size if no mapping
      [width, height] = sizes[0];
    }

    return (
      <Box
        className={classes.testRoot}
        data-component="AdSlot"
        sx={{
          width: `${width}px`,
          height: `${height}px`,
        }}
      >
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          className={classes.testSkeleton}
        />
        <Box className={classes.testUnitName}>
          {showAdUnit && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              className={classes.testUnitSize}
            >
              {adUnit}
            </Typography>
          )}
          <Typography variant="caption" color="text.primary" display="block">
            {getSizeName(width, height)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {width}×{height}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Production mode: Real GPT ad slot
  return <div id={divId} className={classes.root} data-component="AdSlot" />;
};
